import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Input,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
} from "@unified-checkout/design-system";
import { ImageUploader } from "@app/dashboard/components/shared/ImageUploader";
import { getSectorIndustry } from "@app/dashboard/services/inner_layout/sector-industry";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import type { BusinessInfoProps, SectorIndustryOption } from "@app/dashboard/types/innerLayout/business_profile";
import { validateDescription } from "../utils";
import {
  MAX_BUSINESS_DESCRIPTION_LENGTH,
  MAX_LOGO_SIZE,
  MIN_LOGO_DIMENSION,
} from "@app/dashboard/constants/business-profile";
import styles from "../business-profile.module.scss";

export const BusinessInfo = ({
  formState,
  formErrors,
  setFormState,
  setFormErrors,
  onLogoWasDeletedChange,
  originalLogoUrl,
}: BusinessInfoProps) => {
  const { t } = useTranslation();

  const [logoUrl, setLogoUrl] = useState<string | undefined>(originalLogoUrl || undefined);

  const {
    isFetching: isSectorIndustryFetching,
    isLoading: isSectorIndustryLoading,
    data: sectorIndustryResponse,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SECTOR_INDUSTRY],
    queryFn: () => getSectorIndustry(),
  });

  const accountTypeOptions = useMemo(
    () => [
      { value: "1" as const, label: t("innerLayout.businessProfilePage.businessInfo.accountType.options.corporate") },
      { value: "0" as const, label: t("innerLayout.businessProfilePage.businessInfo.accountType.options.individual") },
    ],
    [t]
  );

  const sectorIndustryData = useMemo(
    () => sectorIndustryResponse?.data ?? [],
    [sectorIndustryResponse?.data]
  );

  const sectorOptions: SectorIndustryOption[] = sectorIndustryData.map((sectorItem: { id: number; title: string }) => ({
    value: sectorItem.id,
    label: sectorItem.title,
  }));

  const industryOptions: SectorIndustryOption[] = formState.sector
    ? (() => {
      const selectedSectorData = sectorIndustryData.find(
        (sectorItem: { id: number; industries?: { id: number; title: string }[] }) => sectorItem.id === formState.sector
      );
      return (
        selectedSectorData?.industries?.map((industryItem: { id: number; title: string }) => ({
          value: industryItem.id,
          label: industryItem.title,
        })) || []
      );
    })()
    : [];

  useEffect(() => {
    if (formState.logoImage instanceof File) {
      setLogoUrl(URL.createObjectURL(formState.logoImage));
    } else if (!originalLogoUrl) {
      setLogoUrl(undefined);
    } else {
      setLogoUrl(originalLogoUrl);
    }
  }, [formState.logoImage, originalLogoUrl]);

  useEffect(() => {
    return () => {
      if (logoUrl) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logoUrl]);

  useEffect(() => {
    if (formState.sector === "") {
      if (formState.industry !== "") {
        setFormState((prev) => ({ ...prev, industry: "" }));
      }
      return;
    }
    if (sectorIndustryData?.length === 0) return;

    const selectedSectorData = sectorIndustryData.find(
      (sectorItem: { id: number; industries?: { id: number }[] }) => sectorItem.id === formState.sector
    );
    const isIndustryValid = selectedSectorData?.industries?.some(
      (industryItem: { id: number }) => industryItem.id === formState.industry
    );
    if (!isIndustryValid && formState.industry !== "") {
      setFormState((prev) => ({ ...prev, industry: "" }));
    }
  }, [formState.sector, formState.industry, sectorIndustryData, setFormState]);

  const updateField = <K extends keyof typeof formState>(field: K, value: (typeof formState)[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const getSectorDisplayText = () => {
    if (formState.sector === "") return "";
    const option = sectorOptions.find((o: SectorIndustryOption) => o.value === formState.sector);
    return option?.label ?? "";
  };

  const getIndustryDisplayText = () => {
    if (formState.industry === "" || formState.sector === "") return "";
    const option = industryOptions.find((o: SectorIndustryOption) => o.value === formState.industry);
    return option?.label ?? "";
  };

  const handleLogoFileSelect = ({ file }: { e?: ChangeEvent<HTMLInputElement>; file?: File; url?: string }) => {
    setFormState((prev) => ({ ...prev, logoImage: file || null }));
  };

  const handleLogoDelete = () => {
    onLogoWasDeletedChange(true);
    setFormState((prev) => ({ ...prev, logoImage: null }));
  };

  const handleDescriptionChange = (value: string) => {
    if (value.length <= MAX_BUSINESS_DESCRIPTION_LENGTH) {
      updateField("businessDescription", value);
      validateDescription(value, MAX_BUSINESS_DESCRIPTION_LENGTH, setFormErrors, t);
    }
  };

  const handleDescriptionBlur = (value: string) => {
    validateDescription(value, MAX_BUSINESS_DESCRIPTION_LENGTH, setFormErrors, t);
  };

  const isSectorDisabled = isSectorIndustryFetching || isSectorIndustryLoading;
  const isIndustryDisabled = !formState.sector || isSectorDisabled;

  return (
    <div className={styles["business-profile__section"]}>
      <div className={styles["business-profile__section__label"]}>
        <p>{t('innerLayout.businessProfilePage.businessInfo.title')}</p>
      </div>
      <div className={styles["business-profile__section__form"]}>
        <div className={styles["business-profile__section__form__content"]}>
          {/* Logo Uploader */}
          <div className={styles["business-profile__logo-uploader"]}>
            <ImageUploader
              size="sm"
              imgUrl={logoUrl}
              imgName="Business logo"
              onFileSelect={handleLogoFileSelect}
              onDeleteHandler={handleLogoDelete}
              label={t('innerLayout.businessProfilePage.businessInfo.logo.label')}
              hint={t('innerLayout.businessProfilePage.businessInfo.logo.hint')}
              allowedSize={MAX_LOGO_SIZE}
              minWidth={MIN_LOGO_DIMENSION}
              minHeight={MIN_LOGO_DIMENSION}
              maxWidth={5000}
              maxHeight={5000}
              showReplaceBtn={true}
            />
          </div>

          {/* Business Name and Description Row */}
          <div className={styles["business-profile__row"]}>
            <div className={styles["business-profile__field"]}>
              <Input
                name="businessName"
                label={t('innerLayout.businessProfilePage.businessInfo.businessName.label')}
                value={formState.businessName}
                onChange={(e) => updateField("businessName", e.target.value)}
              />
            </div>
            <div className={styles["business-profile__field"]}>
              <Input
                name="businessDescription"
                label={t('innerLayout.businessProfilePage.businessInfo.businessDescription.label')}
                value={formState.businessDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                onBlur={(e) => handleDescriptionBlur(e.target.value)}
                error={formErrors.businessDescription}
                status={formErrors.businessDescription ? 'error' : 'default'}
              />
            </div>
          </div>

          {/* Account Type, Sector, Industry Row */}
          <div className={styles["business-profile__row"]}>
            {/* Account Type */}
            <div className={styles["business-profile__field"]}>
              <Dropdown
                label={t('innerLayout.businessProfilePage.businessInfo.accountType.label')}
                width="full"
              >
                <DropdownButton
                  type="input"
                  text={accountTypeOptions.find((o) => o.value === formState.accountType)?.label ?? formState.accountType}
                  placeholder={t('innerLayout.businessProfilePage.businessInfo.accountType.placeholder')}
                />
                <DropdownMenu>
                  {accountTypeOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      text={option.label}
                      onClickHandler={() => updateField("accountType", option.value)}
                    />
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
            {/* Sector */}
            <div className={styles["business-profile__field"]}>
              <Dropdown
                label={t('innerLayout.businessProfilePage.businessInfo.sector.label')}
                width="full"
              >
                <DropdownButton
                  type="input"
                  text={getSectorDisplayText()}
                  placeholder={t('innerLayout.businessProfilePage.businessInfo.sector.placeholder')}
                  disabled={isSectorDisabled}
                />
                <DropdownMenu>
                  {sectorOptions.map((option: SectorIndustryOption) => (
                    <DropdownMenuItem
                      key={option.value}
                      text={option.label}
                      onClickHandler={() => updateField("sector", option.value)}
                    />
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
            {/* Industry */}
            <div className={styles["business-profile__field"]}>
              <Dropdown
                label={t('innerLayout.businessProfilePage.businessInfo.industry.label')}
                width="full"
              >
                <DropdownButton
                  type="input"
                  text={getIndustryDisplayText()}
                  placeholder={
                    !formState.sector
                      ? t('innerLayout.businessProfilePage.businessInfo.industry.selectSector')
                      : t('innerLayout.businessProfilePage.businessInfo.industry.placeholder')
                  }
                  disabled={isIndustryDisabled}
                />
                <DropdownMenu>
                  {industryOptions.map((option: SectorIndustryOption) => (
                    <DropdownMenuItem
                      key={option.value}
                      text={option.label}
                      onClickHandler={() => updateField("industry", option.value)}
                    />
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
